import React, { useEffect, useState } from 'react'
import { AutoSizer, Column, InfiniteLoader, Table } from 'react-virtualized'
import PropTypes from 'prop-types'
import { FaIcon } from 'SharedComponents'

const InfiniteScrollTable = ({
  list,
  isLoading,
  totalListCount,
  tableClassName = 'infinite-table',
  rowHeight = 44,
  headerHeight = 38,
  tableColumns,
  initialSortParam,
  initialSortOrder,
  fetchData,
  dependencyArray = [],
}) => {
  const [sortParam, setSortParam] = useState(initialSortParam)
  const [sortOrder, setSortOrder] = useState(initialSortOrder)

  const isRowLoaded = ({ index }) => !!list[index]
  // InfiniteLoader tracks in-flight ranges through the returned Promise -
  // returning undefined makes it re-request the same range on every scroll
  const loadNextPage = ({ stopIndex }) => Promise.resolve(fetchData(sortParam, sortOrder, stopIndex))
  const loadMoreRows = isLoading ? () => Promise.resolve() : loadNextPage

  const headerRenderer = (title, sortParamHeader, isSortable) => {
    if (!isSortable) {
      return <div className='column-header'>{title}</div>
    }
    // aria-sort exposes the current direction to screen readers
    const active = sortParamHeader === sortParam
    const ariaSort = active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'
    return (
      <div className='column-header' aria-sort={ariaSort}
           onClick={() => handleChangeSortOrder(sortParamHeader)}>
        {title}
        {getSortItem(sortParamHeader)}
      </div>
    )
  }

  const handleChangeSortOrder = (column) => {
    setSortParam(column)
    if (column === sortParam) {
      if (sortOrder === 'asc') {
        setSortOrder('des')
      } else {
        setSortOrder('asc')
      }
    } else {
      setSortOrder('asc')
    }
  }

  const getSortItem = (column) => {
    if (column === sortParam) {
      if (sortOrder === 'asc') {
        return (
          <div className='icon-wrapper'>
            <FaIcon icon='sort-up'/>
          </div>
        )
      } else {
        return (
          <div className='icon-wrapper'>
            <FaIcon icon='sort-down'/>
          </div>
        )
      }
    } else {
      return (
        <span className='icon-wrapper'>
          <FaIcon icon='sort'/>
        </span>
      )
    }
  }

  useEffect(() => {
    // Stale-response guard: overlapping fetches (rapid sort changes) can
    // resolve out of order. The effect passes an active() probe as 4th arg -
    // fetchData implementations should check it before committing a response
    // to state. Callers that ignore the extra argument keep current behavior.
    let active = true
    fetchData(sortParam, sortOrder, 0, () => active)
    return () => { active = false }
  }, [sortOrder, sortParam, ...dependencyArray])

  return (
    <div className='infinite-table-main-div'>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={totalListCount}>
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => (
              <Table
                ref={registerChild}
                onRowsRendered={onRowsRendered}
                height={height}
                className={tableClassName}
                rowCount={list.length}
                rowGetter={({ index }) => list[index] || {}}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                width={width}
              >
                {
                  tableColumns.map((column) => {
                    return (
                      <Column
                        key={column['dataKey']}
                        dataKey={column['dataKey']}
                        width={column['width']}
                        flexGrow={column['flexGrow']}
                        headerRenderer={() => headerRenderer(column['columnTitle'], column['sortKey'], column['isSortable'])}
                        className={column['className']}
                        cellRenderer={column['cellRenderer'] || (({ cellData }) => <span>{cellData}</span>)}
                      />
                    )
                  })
                }
              </Table>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  )
}

InfiniteScrollTable.propTypes = {
  getSortItem: PropTypes.func,
  handleChangeSortOrder: PropTypes.func,
  loadNextPage: PropTypes.func,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  list: PropTypes.array,
  isLoading: PropTypes.bool,
  totalListCount: PropTypes.number,
  tableClassName: PropTypes.string,
  tableColumns: PropTypes.array,
  initialSortParam: PropTypes.string,
  sortOrder: PropTypes.string,
  initialSortOrder: PropTypes.string,
  fetchData: PropTypes.func.isRequired,
  dependencyArray: PropTypes.array,
}

export default InfiniteScrollTable
